import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MarkdownView from "@/components/MarkdownView";
import { Bell, AlertTriangle, FileText, PenLine, MailWarning } from "lucide-react";

const fmtHour = (h) => {
  const ampm = h < 12 ? "AM" : "PM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
};
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);

export default function NotificationBell() {
  const { t } = useTranslation();
  const [data, setData] = useState({ at_risk: [], reports: [], unread_count: 0, at_risk_count: 0, needs_log_today: false, expiring_invites: [], expiring_invites_count: 0 });
  const [open, setOpen] = useState(false);
  const [viewReport, setViewReport] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const now = new Date();
      const p = (n) => String(n).padStart(2, "0");
      const local_date = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
      const res = await api.get("/notifications", { params: { local_hour: now.getHours(), local_date } });
      setData(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const onOpen = async (o) => {
    setOpen(o);
    if (o) {
      try { await api.post("/notifications/seen"); } catch { /* ignore */ }
    } else {
      load();
    }
  };

  const badge = data.unread_count + data.at_risk_count + (data.needs_log_today ? 1 : 0) + (data.expiring_invites_count || 0);
  const openReport = async (id) => {
    setOpen(false);
    try {
      const res = await api.get(`/team/reports/${id}`);
      setViewReport(res.data);
    } catch (err) { toast.error(apiError(err, t("notif.reportNA"))); }
  };

  const setPref = async (key, val) => {
    const next = { ...data.prefs, [key]: val };
    setData((d) => ({ ...d, prefs: next }));
    try { await api.put("/notifications/preferences", next); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button className="relative text-[#6E6E80] hover:text-[#202123] transition-colors" data-testid="notif-bell" aria-label={t("notif.title")}>
          <Bell className="h-5 w-5" />
          {badge > 0 && (
            <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white" data-testid="notif-panel">
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="font-display font-semibold text-navy">{t("notif.title")}</p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {!data.needs_log_today && data.at_risk.length === 0 && data.reports.length === 0 && (data.expiring_invites?.length || 0) === 0 && (
            <p className="px-4 py-8 text-center text-sm text-slatebody" data-testid="notif-empty">{t("notif.allCaught")}</p>
          )}

          {(data.expiring_invites?.length || 0) > 0 && (
            <div className="px-4 py-3 border-b border-slate-100" data-testid="notif-expiring-invites">
              <p className="font-mono text-[10px] uppercase text-slatebody mb-2">{t("notif.invitesExpiring")}</p>
              {data.expiring_invites.slice(0, 5).map((inv, i) => {
                const days = (() => {
                  const ms = new Date(inv.invite_expires) - new Date();
                  const d = Math.max(0, Math.ceil(ms / 86400000));
                  return d <= 1 ? t("notif.within1Day") : t("notif.inNDays", { n: d });
                })();
                return (
                  <div key={i} className="flex items-start gap-2 py-1.5" data-testid={`notif-invite-${i}`}>
                    <MailWarning className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-navy leading-snug truncate">{inv.full_name || inv.email}</p>
                      <p className="text-[11px] text-slatebody truncate">{t("notif.inviteExpires", { when: days })}</p>
                    </div>
                  </div>
                );
              })}
              <button onClick={() => { setOpen(false); navigate("/app/organization"); }} data-testid="notif-invite-resend"
                className="text-xs text-[#202123] font-medium hover:underline mt-1.5">{t("notif.manageInvites")}</button>
            </div>
          )}

          {data.needs_log_today && (
            <div className="px-4 py-3 border-b border-slate-100" data-testid="notif-reminder">
              <div className="flex items-start gap-2">
                <PenLine className="h-4 w-4 text-[#202123] shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-navy leading-snug">{t("notif.notLoggedToday")}</p>
                  <button onClick={() => { setOpen(false); navigate("/app/log"); }} data-testid="reminder-log-now"
                    className="text-xs text-[#202123] font-medium hover:underline mt-1">{t("notif.logNow")}</button>
                </div>
              </div>
            </div>
          )}

          {data.at_risk.length > 0 && (
            <div className="px-4 py-3">
              <p className="font-mono text-[10px] uppercase text-slatebody mb-2">{t("notif.atRisk")}</p>
              {data.at_risk.slice(0, 5).map((o, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5" data-testid={`notif-atrisk-${i}`}>
                  <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${o.status === "overdue" ? "text-red-500" : "text-amber-500"}`} />
                  <div className="min-w-0">
                    <p className="text-sm text-navy leading-snug truncate">{o.name}</p>
                    <p className="text-[11px] text-slatebody">{o.status.replace("_", " ")} · {t("notif.due")} {o.deadline}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {data.reports.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100">
              <p className="font-mono text-[10px] uppercase text-slatebody mb-2">{t("notif.recentTeamReports")}</p>
              {data.reports.slice(0, 6).map((r) => (
                <button key={r.id} onClick={() => openReport(r.id)} data-testid={`notif-report-${r.id}`}
                  className="w-full text-left flex items-start gap-2 py-1.5 hover:bg-background rounded-md px-1 -mx-1">
                  <FileText className="h-4 w-4 text-[#202123] shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-navy leading-snug truncate">{r.title}{r.is_new && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#ECECEC] align-middle" />}</p>
                    <p className="text-[11px] text-slatebody truncate">{r.author_name}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-slate-100 px-4 py-3 space-y-2.5" data-testid="notif-prefs">
          <div className="flex items-center justify-between">
            <span className="text-sm text-navy">{t("notif.dailyReminder")}</span>
            <Switch checked={data.prefs?.daily_reminder ?? true} onCheckedChange={(v) => setPref("daily_reminder", v)} data-testid="pref-daily-reminder" />
          </div>
          {(data.prefs?.daily_reminder ?? true) && (
            <div className="flex items-center justify-between pl-1" data-testid="reminder-time-row">
              <span className="text-sm text-slatebody">{t("notif.remindAt")}</span>
              <Select value={String(data.prefs?.reminder_hour ?? 17)} onValueChange={(v) => setPref("reminder_hour", Number(v))}>
                <SelectTrigger className="h-8 w-28 text-sm" data-testid="reminder-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {HOUR_OPTIONS.map((h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`reminder-hour-${h}`}>{fmtHour(h)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm text-navy">{t("notif.atRiskAlerts")}</span>
            <Switch checked={data.prefs?.at_risk ?? true} onCheckedChange={(v) => setPref("at_risk", v)} data-testid="pref-at-risk" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-navy">{t("notif.weeklyRecap")}</span>
            <Switch checked={data.prefs?.weekly_recap_email ?? true} onCheckedChange={(v) => setPref("weekly_recap_email", v)} data-testid="pref-weekly-recap-email" />
          </div>
          {data.can_team && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-navy">{t("notif.teamPings")}</span>
              <Switch checked={data.prefs?.team_reports ?? true} onCheckedChange={(v) => setPref("team_reports", v)} data-testid="pref-team-reports" />
            </div>
          )}
        </div>
      </PopoverContent>

      <Dialog open={!!viewReport} onOpenChange={(o) => !o && setViewReport(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="notif-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{viewReport?.title}</DialogTitle>
            <DialogDescription>{viewReport?.author_name ? t("notif.byAuthor", { name: viewReport.author_name }) : t("notif.teamReport")}{viewReport?.range_type ? ` · ${viewReport.range_type}` : ""}</DialogDescription>
          </DialogHeader>
          {viewReport && <MarkdownView content={viewReport.content} />}
        </DialogContent>
      </Dialog>
    </Popover>
  );
}
